/**
 * @zendir/ui - Typography Component
 *
 * AstroUXDS-compliant typography component providing consistent text styling
 * across the design system. All sizing uses rem units per Astro specifications.
 *
 * Typography Scale (Astro UX Design System):
 * - Display: Large hero text (display1, display2)
 * - Heading: Section headers (h1-h6)
 * - Body: Content text (body1, body2, body3)
 * - Control: UI element labels
 * - Mono: Tabular/code data
 *
 * @see https://www.astrouxds.com/foundations/typography/
 */

import React from "react";
import { useTheme } from "../../theme/ThemeProvider";

// ============================================================================
// Typography Variants (AstroUXDS Compliant)
// ============================================================================

/**
 * Typography variant following Astro UX Design System
 * Maps directly to official Astro typography tokens
 */
export type TypographyVariant =
  // Display - Large hero text
  | "display1"
  | "display2"
  // Headings
  | "h1"
  | "h1Bold"
  | "h2"
  | "h3"
  | "h4"
  | "h5"
  | "h6"
  // Body text
  | "body1"
  | "body1Bold"
  | "body2"
  | "body2Bold"
  | "body3"
  | "body3Bold"
  // Control (UI labels)
  | "control1"
  | "control1Bold"
  // Compact variants for dense UIs (cards, badges)
  | "compact" // 10px - for tight spaces
  | "compactBold"
  | "micro" // 9px - for very tight spaces
  | "microBold"
  // Monospace for data/code
  | "mono"
  | "monoSmall"
  | "monoCompact";

/**
 * Semantic element to render
 */
export type TypographyElement =
  | "h1"
  | "h2"
  | "h3"
  | "h4"
  | "h5"
  | "h6"
  | "p"
  | "span"
  | "div"
  | "label"
  | "code"
  | "pre";

/**
 * Text color variants
 */
export type TypographyColor =
  | "primary"
  | "secondary"
  | "tertiary"
  | "muted"
  | "inverse"
  | "inherit"
  // Status colors
  | "normal"
  | "standby"
  | "caution"
  | "serious"
  | "critical"
  | "off";

export interface TypographyProps {
  /** Typography variant (required) */
  variant: TypographyVariant;
  /** HTML element to render */
  as?: TypographyElement;
  /** Text color */
  color?: TypographyColor;
  /** Enable tabular figures for numeric alignment */
  tabular?: boolean;
  /** Truncate with ellipsis */
  truncate?: boolean;
  /** Text alignment */
  align?: "left" | "center" | "right";
  /** Disable text wrapping */
  noWrap?: boolean;
  /** Transform text case */
  transform?: "none" | "uppercase" | "lowercase" | "capitalize";
  /** Additional CSS styles */
  style?: React.CSSProperties;
  /** Additional CSS class */
  className?: string;
  /** Children */
  children?: React.ReactNode;
  /** Test ID */
  "data-testid"?: string;
}

// ============================================================================
// Font stacks
// ============================================================================

/**
 * Primary body font stack used by all components.
 *
 * Public Sans is the lead face for body text; Roboto is the fallback that
 * activates when web fonts fail to load (Public Sans is not installed
 * locally on most machines, but Roboto is on Android by default — and
 * AstroUXDS specifies Roboto as the canonical engineering-grade fallback).
 * Below that, the OS-native system fonts complete the chain so the UI
 * never falls through to Times.
 *
 * Read by `var(--font-family-primary)` so consumers can override at the
 * `:root` level (set by `<ThemeProvider>` from `tokens.typography.fontFamily.primary`).
 */
export const FONT_FAMILY_PRIMARY =
  'var(--font-family-primary, "Public Sans", "Roboto", -apple-system, BlinkMacSystemFont, "Segoe UI", "Helvetica Neue", Arial, sans-serif)';

/**
 * Monospace stack for tabular data, code, and numeric readouts.
 * Roboto Mono leads (AstroUXDS canonical), then OS-native mono fonts.
 */
export const FONT_FAMILY_MONO =
  '"Roboto Mono", "SF Mono", "Consolas", "Liberation Mono", monospace';

// ============================================================================
// AstroUXDS Font Weights
// Official weights: 300 (Light), 400 (Regular), 500 (Medium), 700 (Bold)
// Note: Astro does NOT use 600 (semibold) - use 500 or 700 instead
// ============================================================================

export const FONT_WEIGHTS = {
  light: 300,
  regular: 400,
  medium: 500,
  bold: 700,
} as const;

// ============================================================================
// Typography Style Definitions
// ============================================================================

interface TypographyStyleDef {
  fontSize: string;
  fontWeight: number;
  lineHeight: string;
  letterSpacing: string;
  fontFamily?: string;
}

/**
 * Get typography styles for a variant
 * All sizes in rem for Astro compliance
 */
function getTypographyStyles(variant: TypographyVariant): TypographyStyleDef {
  const styles: Record<TypographyVariant, TypographyStyleDef> = {
    // Display variants
    display1: {
      fontSize: "3.75rem",
      fontWeight: 300,
      lineHeight: "4.375rem",
      letterSpacing: "-0.5px",
      fontFamily: 'var(--font-family-heading, "Sora", "Roboto", sans-serif)',
    },
    display2: {
      fontSize: "3rem",
      fontWeight: 400,
      lineHeight: "3.5rem",
      letterSpacing: "0",
      fontFamily: 'var(--font-family-heading, "Sora", "Roboto", sans-serif)',
    },

    // Heading variants
    h1: {
      fontSize: "2.125rem",
      fontWeight: 400,
      lineHeight: "2.5rem",
      letterSpacing: "0.25px",
      fontFamily: 'var(--font-family-heading, "Sora", "Roboto", sans-serif)',
    },
    h1Bold: {
      fontSize: "2.125rem",
      fontWeight: 700,
      lineHeight: "2.5rem",
      letterSpacing: "0.25px",
      fontFamily: 'var(--font-family-heading, "Sora", "Roboto", sans-serif)',
    },
    h2: {
      fontSize: "1.5rem",
      fontWeight: 400,
      lineHeight: "1.75rem",
      letterSpacing: "0",
      fontFamily: 'var(--font-family-heading, "Sora", "Roboto", sans-serif)',
    },
    h3: {
      fontSize: "1.25rem",
      fontWeight: 500,
      lineHeight: "1.5rem",
      letterSpacing: "0.15px",
      fontFamily: 'var(--font-family-heading, "Sora", "Roboto", sans-serif)',
    },
    h4: {
      fontSize: "1.25rem",
      fontWeight: 300,
      lineHeight: "1.5rem",
      letterSpacing: "0.15px",
      fontFamily: 'var(--font-family-heading, "Sora", "Roboto", sans-serif)',
    },
    h5: {
      fontSize: "1.125rem",
      fontWeight: 400,
      lineHeight: "1.5rem",
      letterSpacing: "0",
      fontFamily: 'var(--font-family-heading, "Sora", "Roboto", sans-serif)',
    },
    h6: {
      fontSize: "1.125rem",
      fontWeight: 300,
      lineHeight: "1.5rem",
      letterSpacing: "0",
      fontFamily: 'var(--font-family-heading, "Sora", "Roboto", sans-serif)',
    },

    // Body variants
    body1: {
      fontSize: "1rem",
      fontWeight: 400,
      lineHeight: "1.5rem",
      letterSpacing: "0.5px",
    },
    body1Bold: {
      fontSize: "1rem",
      fontWeight: 700,
      lineHeight: "1.5rem",
      letterSpacing: "0.5px",
    },
    body2: {
      fontSize: "0.875rem",
      fontWeight: 400,
      lineHeight: "1.25rem",
      letterSpacing: "0.5px",
    },
    body2Bold: {
      fontSize: "0.875rem",
      fontWeight: 700,
      lineHeight: "1.25rem",
      letterSpacing: "0.5px",
    },
    body3: {
      fontSize: "0.75rem",
      fontWeight: 400,
      lineHeight: "1rem",
      letterSpacing: "0",
    },
    body3Bold: {
      fontSize: "0.75rem",
      fontWeight: 700,
      lineHeight: "1rem",
      letterSpacing: "0",
    },

    // Control variants (UI labels)
    control1: {
      fontSize: "1rem",
      fontWeight: 400,
      lineHeight: "1.25rem",
      letterSpacing: "0.5px",
    },
    control1Bold: {
      fontSize: "1rem",
      fontWeight: 700,
      lineHeight: "1.25rem",
      letterSpacing: "0.5px",
    },

    // Compact variants for dense UIs (cards, badges, data labels)
    compact: {
      fontSize: "0.625rem",
      fontWeight: 400,
      lineHeight: "0.875rem",
      letterSpacing: "0",
    }, // 10px
    compactBold: {
      fontSize: "0.625rem",
      fontWeight: 500,
      lineHeight: "0.875rem",
      letterSpacing: "0",
    },
    micro: {
      fontSize: "0.5625rem",
      fontWeight: 400,
      lineHeight: "0.75rem",
      letterSpacing: "0",
    }, // 9px
    microBold: {
      fontSize: "0.5625rem",
      fontWeight: 500,
      lineHeight: "0.75rem",
      letterSpacing: "0",
    },

    // Monospace variants for data/code
    mono: {
      fontSize: "0.875rem",
      fontWeight: 400,
      lineHeight: "1.25rem",
      letterSpacing: "0",
      fontFamily: FONT_FAMILY_MONO,
    },
    monoSmall: {
      fontSize: "0.75rem",
      fontWeight: 400,
      lineHeight: "1rem",
      letterSpacing: "0",
      fontFamily: FONT_FAMILY_MONO,
    },
    monoCompact: {
      fontSize: "0.625rem",
      fontWeight: 400,
      lineHeight: "0.875rem",
      letterSpacing: "0",
      fontFamily: FONT_FAMILY_MONO,
    },
  };

  return styles[variant];
}

/**
 * Get default HTML element for variant
 */
function getDefaultElement(variant: TypographyVariant): TypographyElement {
  if (variant.startsWith("display")) return "h1";
  if (variant.startsWith("h1")) return "h1";
  if (variant.startsWith("h2")) return "h2";
  if (variant.startsWith("h3")) return "h3";
  if (variant.startsWith("h4")) return "h4";
  if (variant.startsWith("h5")) return "h5";
  if (variant.startsWith("h6")) return "h6";
  if (variant.startsWith("mono")) return "code";
  if (variant.startsWith("control")) return "label";
  return "span";
}

// ============================================================================
// Typography Component
// ============================================================================

/**
 * Typography component providing AstroUXDS-compliant text styling.
 *
 * @example
 * ```tsx
 * // Heading
 * <Typography variant="h3">Section Title</Typography>
 *
 * // Body text
 * <Typography variant="body1" color="secondary">Description text</Typography>
 *
 * // Monospace data with tabular figures
 * <Typography variant="mono" tabular>123.456</Typography>
 *
 * // Compact label for cards
 * <Typography variant="compact" color="tertiary">LABEL</Typography>
 * ```
 */
export const Typography: React.FC<TypographyProps> = ({
  variant,
  as,
  color = "primary",
  tabular = false,
  truncate = false,
  align,
  noWrap = false,
  transform,
  style,
  className,
  children,
  "data-testid": testId,
}) => {
  const { tokens } = useTheme();

  const typographyStyles = getTypographyStyles(variant);
  const Element = as || getDefaultElement(variant);

  // Resolve color
  const getColor = (): string | undefined => {
    if (color === "inherit") return "inherit";
    if (color === "primary") return tokens.colors.text.primary;
    if (color === "secondary") return tokens.colors.text.secondary;
    if (color === "tertiary") return tokens.colors.text.tertiary;
    if (color === "muted")
      return tokens.colors.text.muted || tokens.colors.text.tertiary;
    if (color === "inverse") return tokens.colors.text.inverse;
    // Status colors
    if (color === "normal") return tokens.colors.status.normal;
    if (color === "standby") return tokens.colors.status.standby;
    if (color === "caution") return tokens.colors.status.caution;
    if (color === "serious") return tokens.colors.status.serious;
    if (color === "critical") return tokens.colors.status.critical;
    if (color === "off") return tokens.colors.status.off;
    return undefined;
  };

  const combinedStyle: React.CSSProperties = {
    fontFamily: typographyStyles.fontFamily || FONT_FAMILY_PRIMARY,
    fontSize: typographyStyles.fontSize,
    fontWeight: typographyStyles.fontWeight,
    lineHeight: typographyStyles.lineHeight,
    letterSpacing: typographyStyles.letterSpacing,
    color: getColor(),
    margin: 0,
    padding: 0,
    // Tabular figures for numeric alignment
    ...(tabular && {
      fontVariantNumeric: "tabular-nums",
      fontFeatureSettings: '"tnum"',
    }),
    // Truncation
    ...(truncate && {
      overflow: "hidden",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
    }),
    // No wrap
    ...(noWrap &&
      !truncate && {
        whiteSpace: "nowrap",
      }),
    // Text alignment
    ...(align && { textAlign: align }),
    // Text transform
    ...(transform && { textTransform: transform }),
    // Custom styles (can override defaults)
    ...style,
  };

  return (
    <Element className={className} style={combinedStyle} data-testid={testId}>
      {children}
    </Element>
  );
};

// ============================================================================
// Convenience Components
// ============================================================================

/** Display 1 - Largest display text */
export const Display1: React.FC<Omit<TypographyProps, "variant">> = (props) => (
  <Typography variant="display1" {...props} />
);

/** Display 2 - Large display text */
export const Display2: React.FC<Omit<TypographyProps, "variant">> = (props) => (
  <Typography variant="display2" {...props} />
);

/** Heading 1 */
export const H1: React.FC<
  Omit<TypographyProps, "variant"> & { bold?: boolean }
> = ({ bold, ...props }) => (
  <Typography variant={bold ? "h1Bold" : "h1"} {...props} />
);

/** Heading 2 */
export const H2: React.FC<Omit<TypographyProps, "variant">> = (props) => (
  <Typography variant="h2" {...props} />
);

/** Heading 3 */
export const H3: React.FC<Omit<TypographyProps, "variant">> = (props) => (
  <Typography variant="h3" {...props} />
);

/** Heading 4 */
export const H4: React.FC<Omit<TypographyProps, "variant">> = (props) => (
  <Typography variant="h4" {...props} />
);

/** Heading 5 */
export const H5: React.FC<Omit<TypographyProps, "variant">> = (props) => (
  <Typography variant="h5" {...props} />
);

/** Heading 6 */
export const H6: React.FC<Omit<TypographyProps, "variant">> = (props) => (
  <Typography variant="h6" {...props} />
);

/** Body text level 1 (16px) */
export const Body1: React.FC<
  Omit<TypographyProps, "variant"> & { bold?: boolean }
> = ({ bold, ...props }) => (
  <Typography variant={bold ? "body1Bold" : "body1"} {...props} />
);

/** Body text level 2 (14px) */
export const Body2: React.FC<
  Omit<TypographyProps, "variant"> & { bold?: boolean }
> = ({ bold, ...props }) => (
  <Typography variant={bold ? "body2Bold" : "body2"} {...props} />
);

/** Body text level 3 (12px) */
export const Body3: React.FC<
  Omit<TypographyProps, "variant"> & { bold?: boolean }
> = ({ bold, ...props }) => (
  <Typography variant={bold ? "body3Bold" : "body3"} {...props} />
);

/** Compact text for dense UIs (10px) */
export const Compact: React.FC<
  Omit<TypographyProps, "variant"> & { bold?: boolean }
> = ({ bold, ...props }) => (
  <Typography variant={bold ? "compactBold" : "compact"} {...props} />
);

/** Micro text for very tight spaces (9px) */
export const Micro: React.FC<
  Omit<TypographyProps, "variant"> & { bold?: boolean }
> = ({ bold, ...props }) => (
  <Typography variant={bold ? "microBold" : "micro"} {...props} />
);

/** Monospace text for data/code */
export const Mono: React.FC<
  Omit<TypographyProps, "variant"> & { size?: "normal" | "small" | "compact" }
> = ({
  size = "normal",
  tabular = true, // Default to tabular for mono
  ...props
}) => {
  const variant =
    size === "compact"
      ? "monoCompact"
      : size === "small"
        ? "monoSmall"
        : "mono";
  return <Typography variant={variant} tabular={tabular} {...props} />;
};

/** Data value text - monospace with tabular figures (for numeric displays) */
export const DataText: React.FC<
  Omit<TypographyProps, "variant" | "tabular"> & {
    size?: "large" | "normal" | "small" | "compact";
  }
> = ({ size = "normal", ...props }) => {
  const variantMap: Record<string, TypographyVariant> = {
    large: "mono",
    normal: "mono",
    small: "monoSmall",
    compact: "monoCompact",
  };
  return <Typography variant={variantMap[size]} tabular {...props} />;
};

/** Label text for form controls and card headers */
export const Label: React.FC<
  Omit<TypographyProps, "variant"> & { bold?: boolean }
> = ({ bold, ...props }) => (
  <Typography
    variant={bold ? "control1Bold" : "control1"}
    as="label"
    {...props}
  />
);

export default Typography;
